import React, { useRef, useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'
import { logoutNative } from '@/lib/nativeAuth'

/**
 * User avatar button that opens a dropdown with email, theme toggle,
 * team settings (Studio only), and sign-out. Renders nothing when
 * unauthenticated or while the auth check is in flight.
 */
export const UserMenu: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const platform = usePlatform()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  if (isLoading || !isAuthenticated || !user) return null

  const initials = (user.name ?? user.email ?? '?')
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('')

  const handleSignOut = () => {
    if (platform.isNative) {
      void logoutNative()
      window.location.href = '/login'
    } else {
      // Pass the origin so the post-logout redirect lands the user
      // back on the same host they signed out from.
      const origin = encodeURIComponent(window.location.origin)
      window.location.href = `/api/v1/auth/logout?origin=${origin}`
    }
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-white text-xs font-semibold hover:bg-accent-hover transition-colors duration-150"
        title={user.email}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {user.avatar ? (
          <img
            src={user.avatar}
            alt=""
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          initials
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-xl border border-brd-subtle bg-surface-raised shadow-high z-50 py-1 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-brd-subtle">
            {user.name && (
              <div className="text-sm font-medium text-content-primary truncate">
                {user.name}
              </div>
            )}
            <div className="text-xs text-content-secondary truncate">
              {user.email}
            </div>
          </div>
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="block w-full text-left px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-accent-muted transition-colors duration-150"
          >
            Settings
          </Link>
          <div className="border-t border-brd-subtle" />
          {user.tier === 'studio' && (
            <Link
              to="/team"
              onClick={() => setOpen(false)}
              className="block w-full text-left px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-accent-muted transition-colors duration-150"
            >
              Team settings
            </Link>
          )}
          <div className="border-t border-brd-subtle" />
          <button
            onClick={handleSignOut}
            className="w-full text-left px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-accent-muted transition-colors duration-150"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
